import { Button, Form, Input, InputNumber, Row, Space, Switch, Tag, Typography } from 'antd';
import React, { FC, useCallback, useRef, useState } from 'react';
import SelectStatus from '../../../form/adapters/selectStatus';
import { LineChartOutlined, ArrowRightOutlined, ProfileOutlined } from '@ant-design/icons';
import { useConnection, useEvents, useUserInfo } from '../../../connection/Application';
import { PROGRESS } from '../../../screens/webux/webux-pages/goals/KeyResult';
import dayjs from 'dayjs';
const { TextArea } = Input;

const { Text } = Typography;

export interface KeyResultDetails {
  status?: string;
  id: number | string;
  completed?: number;
  parent: any;
  currentValue: number;
  progress: PROGRESS;
  goalHolder?: any;
  goalMark?: any;
  goalType?: any;
  key?: any;
  treeType?: any;
  reusable?: boolean;
}

export type Props = {
  value?: KeyResultDetails;
  id?: number;
  onCancel: () => void;
};
const KeyUpdateInput: FC<Props> = (props) => {
  const { value, id } = props;
  const viewDetails = useRef<KeyResultDetails>(value as KeyResultDetails);
  const [inputNum, setInputNum] = useState<number>(viewDetails.current.currentValue);
  const [changingStatus, setChangingStatus] = useState<string>(viewDetails.current?.status ?? '');
  const [comment, setComment] = useState<string>('');
  const connection = useConnection();
  const user = useUserInfo();
  const time = dayjs().format('MMM DD, YYYY h:mm a');
  const events = useEvents();

  const createValueChange = useCallback(
    (updates: KeyResultDetails) => {
      let beforeValue: number | string;
      let updatedValue: number | string;
      if (updates?.progress?.type === 'Open/Done') {
        beforeValue = +(updates?.completed ?? 0) === 100 ? 'Done' : 'Open';
        updatedValue = +inputNum === 100 ? 'Done' : 'Open';
      } else {
        beforeValue = updates?.currentValue?.toString() ?? '0';
        updatedValue = inputNum?.toString() ?? '0';
      }
      const data = {
        time: dayjs().unix().toString(),
        date: dayjs().format('MMM DD, YYYY'),
        userId: user?.userId,
        type: 'keyResult',
        keyResultId: id,
        goalId: updates?.parent?.id,
        beforeValue,
        updatedValue: inputNum !== undefined ? updatedValue : beforeValue,
        beforeStatus: updates?.status,
        updatedStatus: changingStatus ?? updates?.status,
        unit: updates?.progress?.unit,
        comment: comment,
        keyResultType: updates?.progress?.type,
        valueChangeType: 'History',
      };
      if (
        changingStatus !== updates?.status ||
        inputNum !== updates?.currentValue ||
        inputNum !== updates?.completed
      ) {
        connection.post(`value-change`, { data }).catch((e) => console.log(e));
      }
    },
    [changingStatus, comment, connection, id, inputNum, user?.userId],
  );

  const saveDetails = useCallback(
    (updates: KeyResultDetails) => {
      const newUpdate = { ...updates };
      delete (newUpdate as any).id;
      delete newUpdate?.parent;
      delete newUpdate?.goalHolder;
      delete newUpdate?.goalMark;
      delete newUpdate?.goalType;
      delete newUpdate?.key;
      delete newUpdate?.reusable;
      delete newUpdate?.treeType;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete newUpdate?.weight;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete newUpdate?.teamWeight;
      connection
        .patch(`goals/keyResult/${id}`, { keyResult: newUpdate })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => props.onCancel());
    },
    [connection, id, props],
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div>
        <Space
          size={30}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline',
            marginTop: 20,
          }}>
          <Space style={{ marginBottom: 24 }}>
            <LineChartOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
            <Text type="secondary">Progress</Text>
          </Space>
          {viewDetails.current?.progress?.type === 'Start and Target Value' ? (
            <Space>
              <Space style={{ marginBottom: 24 }}>
                <Typography>{viewDetails.current?.progress?.startValue}</Typography>
                <ArrowRightOutlined />
              </Space>

              <InputNumber
                defaultValue={viewDetails.current.currentValue}
                value={inputNum}
                style={{ width: 70, marginBottom: 24 }}
                onChange={(e) => {
                  setInputNum(e ?? 0);
                }}
                min={viewDetails.current?.progress?.startValue}
                max={viewDetails.current?.progress?.endValue}
              />

              <Space style={{ marginBottom: 24 }}>
                <ArrowRightOutlined />
                <Typography>{viewDetails.current?.progress?.endValue}</Typography>
              </Space>
            </Space>
          ) : viewDetails.current?.progress?.type === 'Open/Done' ? (
            <Space style={{ display: 'flex', flexDirection: 'row' }}>
              <Switch
                size="small"
                onChange={(val) => {
                  if (val) {
                    setInputNum(100);
                  } else {
                    setInputNum(0);
                  }
                }}
                defaultChecked={+(viewDetails.current?.completed ?? 0) === 100}
              />
              <div style={{ minWidth: '90px' }}>
                <Text>{+(inputNum ?? 0) === 100 ? 'Done' : 'Open'}</Text>
              </div>
            </Space>
          ) : (
            <Space>
              <InputNumber
                defaultValue={viewDetails.current.currentValue}
                style={{ width: 70, marginBottom: 24 }}
                onChange={(e) => {
                  setInputNum(e ?? 0);
                }}
                min={viewDetails.current?.progress?.startValue}
                max={viewDetails.current?.progress?.endValue}
              />
              <Space style={{ marginBottom: 24 }}>
                <ArrowRightOutlined />
                <Typography>{viewDetails.current?.progress?.threshold}</Typography>
              </Space>
            </Space>
          )}
          <Tag
            style={{ height: 33, marginBottom: 24, display: 'flex', alignItems: 'center' }}
            color="">
            {time}
          </Tag>
        </Space>
      </div>
      <div style={{ marginBottom: 20 }}>
        {viewDetails.current?.status && (
          <Space size={30}>
            <Space>
              <ProfileOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Status</Text>
            </Space>
            <SelectStatus
              defaultValue={viewDetails.current.status}
              value={changingStatus}
              bordered={false}
              onChange={(val) => {
                setChangingStatus(val);
              }}
            />
          </Space>
        )}
      </div>
      <Form.Item name="comment">
        <TextArea
          placeholder="Key result status description"
          allowClear
          autoSize={{ minRows: 1, maxRows: 3 }}
          onChange={(e) => setComment(e.target.value)}
        />
      </Form.Item>
      <Row style={{ justifyContent: 'flex-end' }}>
        <Button
          onClick={() => props.onCancel()}
          type="default"
          style={{
            height: 25,
            width: 70,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 15,
            marginRight: 10,
          }}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            let completed = 0;
            if (viewDetails.current?.progress?.type === 'Start and Target Value') {
              completed = Math.round(
                ((inputNum - viewDetails.current?.progress?.startValue) /
                  (viewDetails.current.progress?.endValue -
                    viewDetails.current.progress?.startValue)) *
                  100,
              );
            } else if (viewDetails.current?.progress?.type === 'Threshold') {
              if (viewDetails.current?.progress?.thresholdType === 'Stay Above') {
                completed = inputNum > viewDetails.current?.progress?.threshold ? 100 : 0;
              } else if (viewDetails.current?.progress?.thresholdType === 'Stay Below') {
                completed = inputNum < viewDetails.current?.progress?.threshold ? 100 : 0;
              }
            } else {
              completed = inputNum;
            }
            const newObj: KeyResultDetails = {
              ...viewDetails.current,
              completed: inputNum !== undefined ? completed : viewDetails.current?.completed,
              progress: viewDetails.current.progress,
              currentValue: inputNum,
              status: changingStatus ?? viewDetails.current?.status,
            };
            if (id) {
              createValueChange(viewDetails.current);
              saveDetails(newObj);
              events.emit('VALUE_CHANGE_RELOAD');
            }
          }}
          type="primary"
          style={{
            height: 25,
            width: 70,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 15,
          }}>
          Update
        </Button>
      </Row>
    </div>
  );
};

export default KeyUpdateInput;
